import { ChangeDetectionStrategy, Component } from '@angular/core';
import { RouterLink } from '@angular/router';
import { take, catchError, of, finalize } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { BusinessDetailsComponent } from '../business-details/business-details.component';
import { NgIf } from '@angular/common';
import { CustomizerSettingsService } from '../../../customizer-settings/customizer-settings.service';
import { BaseSearchOptions } from '../../../models/core/table/BaseSearchOptions';
import { FilterDataType } from '../../../models/core/table/FilterDataType';
import { QueryBuilderParams } from '../../../models/core/table/QueryBuilderParams';
import { TableActionButton } from '../../../models/core/table/TableActionButton';
import { TableColumn } from '../../../models/core/table/TableColumn';
import { TableEvent } from '../../../models/core/table/TableEvent';
import { TableSearchEventData } from '../../../models/core/table/TableSearchEventData';
import { Business } from '../../../models/domain/Business';
import { EventTypes } from '../../../models/enums/EventTypes';
import { GroupType } from '../../../models/enums/GroupType';
import { ModalText } from '../../../models/enums/ModalText';
import { BusinessService } from '../../../services/business.service';
import { LoadingService } from '../../../services/loading.service';
import { ModalService } from '../../../services/modal.service';
import { RoleService } from '../../../services/roleService';
import { getSearchOperator } from '../../../utilities/common-functions';
import { TableComponent } from '../../../core/table/table.component';

@Component({
    selector: 'app-business-list',
    imports: [RouterLink, TableComponent, BusinessDetailsComponent, NgIf],
    templateUrl: './business-list.component.html',
    styleUrl: './business-list.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BusinessListComponent {

    //#region Table Variables
    public columns!: TableColumn[];
    public actions!: TableActionButton[];
    public totalRecords = 0;
    public pageIndex = 0;
    public pageSize = 10;
    public pageSizeOptions: number[] = [10, 50, 100];
    public searchOptions!: BaseSearchOptions;
    //#endregion

    businessId = 0;
    business!: Business;

    // Popup Trigger
    classApplied = false;
    isToggled = false;
    showModal = false;
    deleteModalData!: Business;
    ConfirmText = ModalText.ConfirmYes
    CancelText = ModalText.ConfirmNo

    constructor(
        public themeService: CustomizerSettingsService,
        public businessService: BusinessService,
        private modalService: ModalService,
        private toasterService: ToastrService,
        private roleService: RoleService,
        private loadingService: LoadingService

    ) {
        this.initialize();
    }

    ngOnInit(): void {
        this.getData();
    }

    private initialize(): void {
        this.themeService.isToggled$.subscribe(isToggled => {
            this.isToggled = isToggled;
        });

        this.searchOptions = new BaseSearchOptions(undefined, 'name', 0, this.pageIndex, this.pageSize);
        this.buildTable();
    }

    private getData(): void {
        this.loadingService.showLoading();
        this.businessService.get(this.searchOptions).pipe(
            take(1),
            catchError((error) => {
                if (error && error.error) {
                    if (error.error.isCustom) {
                        this.toasterService.warning(error.error.message);
                    } else {
                        this.toasterService.error('Something went wrong!');
                    }
                } else {
                    this.toasterService.error('Something went wrong!');
                }
                return of(null);
            }),
            finalize(() => {
                this.loadingService.hideLoading();
            }),
        ).subscribe(result => {
            if (result) {
                this.totalRecords = result.body?.totalCount ?? 0;
            }
            this.loadingService.hideLoading();
        });
    }

    public buildTable(): void {
        this.columns = [
            new TableColumn('name', 'Name', FilterDataType.String, true, false, (data: Business) => `${data.name}`, true, { tooltip: 'Business Name' }),
            new TableColumn('registrationNo', 'Registration Number', FilterDataType.String, true, false, (data: Business) => `${data.registrationNo}`, true, { tooltip: 'Business Registration Number' }),
            new TableColumn('incorporationDate', 'Incorporation Date', FilterDataType.String, true, false, (data: Business) => `${new Date(data.incorporationDate).toLocaleDateString()}`, true, { tooltip: 'Business Incorporation Date' }),
        ];

        this.actions = [];
        if (this.roleService.canAdd()) {
            this.actions.push(new TableActionButton("edit", (event, value) => this.onEditClick(event, value), (value) => this.onDisabledClick(), "Edit", "Edit"))
        }
        if (this.roleService.canDelete()) {
            this.actions.push(new TableActionButton("delete", (event, value) => this.onDeleteClick(event, value), (value) => this.onDisabledClick(), "Delete", "Delete"))
        }
    }

    public onAddClick(): void {
        this.business = new Business;
        this.toggleClass();
    }

    public onEditClick(event: MouseEvent, data: Business): void {
        event.stopPropagation();
        this.business = data;
        this.toggleClass();
    }

    public onDeleteClick(event: MouseEvent, data: Business): void {
        event.stopPropagation();
        this.modalService.openModal(
            'Delete Confirmation',
            `Are you sure you want to delete <strong> ${data.name} </strong> ?`,
            ModalText.ConfirmYes,
            ModalText.ConfirmNo
        );

        this.modalService.onConfirm.subscribe((isConfirm) => {
            if (isConfirm && data.id) {
                this.loadingService.showLoading();
                this.businessService.delete(data.id).pipe(
                    take(1),
                    catchError((error) => {
                        if (error && error.error) {
                            if (error.error.isCustom) {
                                this.toasterService.warning(error.error.message);
                            } else {
                                this.toasterService.error('Something went wrong!');
                            }
                        } else {
                            this.toasterService.error('Something went wrong!');
                        }
                        return of(null);
                    })
                ).subscribe(result => {
                    if (result?.body) {
                        this.toasterService.info("Record delete successfully.");
                        this.getData();
                    }
                    this.loadingService.hideLoading();
                });
            }
        });
    }

    public onDisabledClick(): boolean {
        return false;
    }

    public tableEvent(event: TableEvent<any>): void {
        if (event.type === EventTypes.TABLE) {
            const eventValue = event.data as TableSearchEventData;

            this.searchOptions.updateByObject(eventValue.baseSearchOptions);

            if (eventValue.columnFilterValues) {
                this.createFilter(eventValue);
            }
            else {
                this.searchOptions.filter = '';
            }
            this.getData();
        }
        if (event.type === EventTypes.CLEAR) {
            this.clearFilters();
            this.getData();
        }
    }

    public clearFilters(): void {
        this.pageIndex = 0;
        this.pageSize = 10;
        this.searchOptions = new BaseSearchOptions(undefined, 'name', 0, this.pageIndex, this.pageSize);
    }

    private createFilter(eventValue: TableSearchEventData): void {
        const queryParams: QueryBuilderParams[] = [];

        for (const [columnName, { dataType, opType, searchValue }] of Object.entries(eventValue.columnFilterValues)) {
            const searchOperator = getSearchOperator(opType.toString());
            if (!searchOperator) continue; // Skip if no valid operator

            queryParams.push({
                fieldName: columnName, // Adjust as necessary for your field mapping
                dataType: dataType,
                searchOperator,
                searchValue: searchValue,
                groupType: GroupType.OR
            });
        }

        this.searchOptions.filter = this.searchOptions.generateQuery(queryParams);
    }

    public toggleClass(): void {
        this.classApplied = !this.classApplied;
    }

    public classAppliedEvent(flag: boolean): void {
        this.classApplied = flag;
    }

    public saveConfirmationEvent(isSaved: boolean): void {
        if (isSaved) {
            this.getData();
        }
    }
}